import { PaginationSchema } from "@/components/paginate/types";
import { z } from "zod";

// Cost Attribution Mode enum
export enum CostAttributionMode {
  Query = "query",
  Warehouse = "warehouse",
}

export const tenantInfoSchema = z.object({
  currency: z.string().default("USD"),
  frontendUrl: z.string(),
  teammatesEnabled: z.boolean().optional().describe("Enable teammates functionality"),
  rbacEnabled: z.boolean().optional().describe("Enable role-based access control"),
  user_group_id: z.coerce.string().nullable(),
  exportLineageEnabled: z.boolean().optional().describe("Enable lineage export functionality"),
  enableBiFeature: z.boolean().optional().describe("Enable business intelligence features"),
  queryListingDefaultDaysCount: z.number().optional(),
  dbtWorkloadEnabled: z.boolean().optional().describe("Enable DBT workload functionality"),
  costAttributionEnabled: z.boolean().optional().describe("Enable cost attribution features"),
  accessRulesDisabled: z.boolean().optional().describe("Disable access rules enforcement"),
  disableAccountCosts: z.boolean().optional().describe("Disable account cost tracking"),
  costAnalysisReportsEnabled: z.boolean().optional().describe("Enable cost analysis reporting"),
  streamlitNotebooksEnabled: z
    .boolean()
    .optional()
    .describe("Enable Streamlit and Notebook workload tabs"),
  storedProceduresEnabled: z.boolean().optional().describe("Enable Stored Procedures workload tab"),
  snowflakeTasksEnabled: z.boolean().optional().describe("Enable Tasks workload tab"),
  enableCustomReports: z.boolean().optional().describe("Enable custom reporting features"),
  enableCostReports: z.boolean().optional().describe("Enable subscriptions functionality"),
  enableTenantSwitching: z.boolean().optional().describe("Enable tenant switching functionality"),
  enableSnowflakeContracts: z.boolean().optional().describe("Enable Snowflake contract features"),
  enableComputePool: z.boolean().optional().describe("Enable compute pool functionality"),
  enableCreditsPage: z.boolean().optional().describe("Enable Credits page"),
  enableOpportunityTicketCreation: z
    .boolean()
    .optional()
    .describe("Enable opportunity ticket creation"),
  enablePriorityTab: z.boolean().optional().describe("Enable the Priority tab on /discover"),
  enableImplementedTab: z
    .boolean()
    .optional()
    .describe("Enable the Implemented tab on /discover (V2)"),
  enableAdvancedGuardRails: z.boolean().optional().describe("Enable advanced guard rail features"),
  staleResourcesEnabled: z.boolean().optional().describe("Enable stale resource detection"),
  enableKnowledgeEngine: z
    .boolean()
    .optional()
    .describe(
      "Enable knowledge engine functionality (Advanced guard rails should also be enabled)"
    ),
  enableKnowledgeEngineLearningsV2: z
    .boolean()
    .optional()
    .describe("Enable improved learnings extraction in knowledge engine"),
  enableKnowledgeEngineMultimodal: z
    .boolean()
    .optional()
    .describe("Enable multimodal (image) processing in knowledge engine"),
  isDemoTenant: z.boolean().optional().describe("Indicates if this is a demo tenant"),
  dbtIntegrationEnabled: z
    .boolean()
    .optional()
    .describe("Enable dbt integration features in freemium"),
  enablePromptLibrary: z.boolean().optional().describe("Enable prompt library in Playground"),
  enableWarehouseSizeRecommendation: z
    .boolean()
    .optional()
    .describe("Enable refactored warehouse size recommendation UI"),
  enableCostAttributionInTeams: z
    .boolean()
    .optional()
    .describe("Enable cost attribution features in teams (access rules and ownership coverage)"),
  enableCloudIntegration: z.boolean().optional().describe("Enable cloud integrations (dbt Cloud)"),
  enableCustomWorkloadsChartAggregation: z
    .boolean()
    .optional()
    .describe("Enable custom workloads chart aggregation in queries page"),
  enableProjectionAgent: z
    .boolean()
    .optional()
    .describe("Enable projection agent suggestions on cost chart"),
  enableAIServicesInCodePage: z
    .boolean()
    .optional()
    .describe("Enable AI Services tab in queries page"),
  queryAiServicesOwnerOnly: z
    .boolean()
    .optional()
    .describe("Restrict the AI Services tab in the Code page to tenant Owners"),
  isDatabricksTenant: z
    .boolean()
    .optional()
    .describe("Indicates if tenant uses Databricks platform"),
  isSparkAnalysisEnabled: z
    .boolean()
    .optional()
    .describe("Indicates if Spark analysis features are enabled"),
  ssoGroupSyncEnabled: z.boolean().optional().describe("Enable SSO Group Sync settings tab"),
  enableAltimateCode: z
    .boolean()
    .optional()
    .describe("Enable Altimate Code integration option in datamate setup"),
  ssoUserRefreshMins: z
    .number()
    .nullable()
    .optional()
    .describe("Auto-logout SSO users after N minutes of inactivity"),
  enableStudioCitations: z
    .boolean()
    .optional()
    .describe("Enable source citations in Studio (Playground) responses"),
  enableStudioShareableLinks: z.boolean().optional().describe("Enable shareable links in Studio"),
  enableStudioScheduledReports: z
    .boolean()
    .optional()
    .describe("Enable scheduled email reports in Studio"),
  showDefaultScheduleBlocks: z
    .boolean()
    .optional()
    .describe("Show default schedule blocks in empty warehouse calendar slots"),
  enableDbtJobsListing: z
    .boolean()
    .optional()
    .describe("Enable dbt Jobs listing tab, Job column in Runs/Models, and Tags in Models"),
  enableSemanticLayer: z.boolean().optional().describe("Enable semantic layer for Playground"),
  disableTaskSelectionAutoTuneDatabricks: z
    .boolean()
    .optional()
    .describe(
      "Hide per-task selection on the Databricks Auto Tune DAG (card click-to-select, dropdown, Apply Changes CTA). Master toggle stays."
    ),
  disableAllPurposeAutotune: z
    .boolean()
    .optional()
    .describe(
      "Kill-switch for the all-purpose cluster Auto Tune feature: hides the Auto Tune tab + header control and never mounts the toggle (no preflight call)."
    ),
  showCloudCosts: z
    .boolean()
    .optional()
    .describe("Show the Cloud Costs hero card and chart series on the Databricks Jobs page"),
});

export type GetTenantInfoResponse = z.infer<typeof tenantInfoSchema>;

export const urlResponse = z.object({
  url: z.string(),
});

export type UrlResponse = z.infer<typeof urlResponse>;

export const FilterZodSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const GetOpportunitiesDynamicFilterResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(FilterZodSchema).nullable(),
});

export type GetOpportunitiesDynamicFilterResponse = z.infer<
  typeof GetOpportunitiesDynamicFilterResponseSchema
>;

export const GetTableLastRefreshedResponseSchema = z.object({
  last_refreshed: z.string().nullable(),
});

export type GetTableLastRefreshedResponse = z.infer<typeof GetTableLastRefreshedResponseSchema>;

export const TableRkFilterResponseSchema = z.object({
  items: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      database: z.string().optional().nullable(),
      table_schema: z.string().optional().nullable(),
    })
  ),
  total: z.number(),
  page: z.number(),
  size: z.number(),
  pages: z.number(),
  filter_type: z.string().optional(),
});

export type TableRkFilterResponse = z.infer<typeof TableRkFilterResponseSchema>;

export const permissionsFullSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  title: z.string(),
  description: z.string(),
  group: z.string().nullable(),
});

export const rolePermissionFullSchema = z.object({
  role_name: z.string(),
  role_description: z.string(),
  role_title: z.string(),
  is_custom: z.boolean(),
  permission: z.array(permissionsFullSchema),
});

export const userLoginOutSchema = z.object({
  id: z.number().nullable(),
  email: z.string(),
  auth_token: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  is_onboarded: z.boolean(),
  tz: z.string().nullable(),
  lang: z.string().nullable(),
  uuid: z.string(),
  role_FK: rolePermissionFullSchema.nullable(),
});

export type UserLoginOutResponse = z.infer<typeof userLoginOutSchema>;

export const StandardResponseSchema = z.object({
  ok: z.boolean(),
});

export type StandardResponse = z.infer<typeof StandardResponseSchema>;

export const StandardResponseWithWarningSchema = z.object({
  ok: z.boolean(),
  warning: z.string().optional(),
});

export type StandardResponseWithWarning = z.infer<typeof StandardResponseWithWarningSchema>;

export const pageSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({ items: z.array(itemSchema), ...PaginationSchema.shape });

export const CompanySchema = z.object({
  name: z.string(),
});

export const CompanyListItemSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  country: z.string(),
});

export const CompanyListResponseSchema = pageSchema(CompanyListItemSchema);

export const CompanyInfoResponseSchema = z.object({
  other_tenants_in_group: z.array(CompanySchema),
});

export type Company = z.infer<typeof CompanySchema>;
export type CompanyListItem = z.infer<typeof CompanyListItemSchema>;
export type CompanyListResponse = z.infer<typeof CompanyListResponseSchema>;
export type CompanyInfoResponse = z.infer<typeof CompanyInfoResponseSchema>;

export const demoSessionResponseSchema = z.object({
  demoUrl: z.string(),
  sessionId: z.string(),
});

export type DemoSessionResponse = z.infer<typeof demoSessionResponseSchema>;

// Email instance list response schema
export const EmailInstanceSchema = z.object({
  company: z.object({
    name: z.string(),
    existence_time: z.string().optional(),
  }),
  company_url: z.string(),
});

export const EmailInstanceListResponseSchema = z.object({
  companies: z.array(EmailInstanceSchema).nullable(),
});

export type EmailInstance = z.infer<typeof EmailInstanceSchema>;
export type EmailInstanceListResponse = z.infer<typeof EmailInstanceListResponseSchema>;
